import { Alert, Button, Empty, Grid, Result, Select, Space, Spin, Steps, Tag, Typography, App as AntApp } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { formatDate, useAcademicYears } from "../app/classes.ts";
import { defaultYears, initialChoice, needsTarget, toDecisions, type RolloverChoice, type RolloverResult, type RolloverRow } from "../app/rollover.ts";
import { RolloverClassCard } from "../components/RolloverClassCard.tsx";

type YearPair = { source: string; target: string };

/** Reviewed year rollover (spec story 21): pick the years, review the proposal per student, confirm. */
export function RolloverPage() {
  const { t } = useTranslation();
  const { modal } = AntApp.useApp();
  const queryClient = useQueryClient();
  const screens = Grid.useBreakpoint();
  const years = useAcademicYears();
  const [picked, setPicked] = useState<Partial<YearPair>>({});
  const [request, setRequest] = useState<YearPair>();
  const [overrides, setOverrides] = useState<Record<string, RolloverChoice>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string[]>>({});
  const [summaryErrors, setSummaryErrors] = useState<string[]>([]);
  const [result, setResult] = useState<RolloverResult>();

  const defaults = defaultYears(years.data ?? []);
  const source = picked.source ?? defaults.source;
  const target = picked.target ?? defaults.target;

  const preview = useQuery({
    queryKey: ["students", "rollover", request?.source, request?.target],
    enabled: request !== undefined,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/students/rollover/preview", {
        params: { query: { SourceYearId: request?.source ?? "", TargetYearId: request?.target ?? "" } },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });

  const rows = preview.data?.students ?? [];
  const choiceOf = (row: RolloverRow): RolloverChoice =>
    row.note === "AlreadyPlaced" ? initialChoice(row) : (overrides[row.studentId] ?? initialChoice(row));
  const choices = Object.fromEntries(rows.map((row) => [row.studentId, choiceOf(row)]));
  const decisions = toDecisions(rows, choices);
  const missing = rows.filter((row) => needsTarget(choiceOf(row))).length;
  const count = (outcome: RolloverChoice["outcome"]) => rows.filter((row) => choiceOf(row).outcome === outcome).length;

  const confirm = useMutation({
    mutationFn: async () => {
      if (!request) throw new Error(t("app.error"));
      const { data, error, response } = await api.POST("/students/rollover", {
        body: { sourceYearId: request.source, targetYearId: request.target, students: decisions },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (data) => {
      setResult(data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["students"] }),
        queryClient.invalidateQueries({ queryKey: ["families"] }),
        queryClient.invalidateQueries({ queryKey: ["audit"] }),
      ]);
    },
    onError: (err) => {
      // Field errors are keyed by the position in the sent list (students[3].targetClassId); map them back to rows.
      const byStudent: Record<string, string[]> = {};
      const other: string[] = [];
      const fieldErrors = err instanceof ApiError ? err.fieldErrors() : {};
      for (const [path, messages] of Object.entries(fieldErrors)) {
        const index = /^students\[(\d+)\]/.exec(path)?.[1];
        const studentId = index === undefined ? undefined : decisions[Number(index)]?.studentId;
        if (studentId) byStudent[studentId] = [...(byStudent[studentId] ?? []), ...messages];
        else other.push(...messages);
      }
      setRowErrors(byStudent);
      setSummaryErrors(Object.keys(fieldErrors).length === 0 && err instanceof ApiError ? err.messages(t("app.error")) : [t("rollover.failed"), ...other]);
    },
  });

  const change = (studentId: string, choice: RolloverChoice) => {
    setOverrides((current) => ({ ...current, [studentId]: choice }));
    setRowErrors((current) => Object.fromEntries(Object.entries(current).filter(([id]) => id !== studentId)));
  };

  const startOver = () => {
    setRequest(undefined);
    setOverrides({});
    setRowErrors({});
    setSummaryErrors([]);
    setResult(undefined);
  };

  const askConfirm = () => {
    if (!preview.data) return;
    modal.confirm({
      title: t("rollover.confirmTitle"),
      content: t("rollover.confirmBody", { start: formatDate(preview.data.targetYear.startDate), end: formatDate(preview.data.sourceYear.endDate) }),
      okText: t("rollover.confirm"),
      cancelText: t("common.cancel"),
      onOk: () => confirm.mutateAsync().catch(() => undefined),
    });
  };

  const step = result ? 2 : request ? 1 : 0;
  const yearOptions = (years.data ?? []).map((y) => ({ value: y.id, label: y.label }));
  const groups = groupByClass(rows);

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Title level={3} style={{ margin: 0 }}>
        {t("rollover.title")}
      </Typography.Title>
      <Steps size="small" current={step} items={[{ title: t("rollover.steps.years") }, { title: t("rollover.steps.review") }, { title: t("rollover.steps.result") }]} />
      <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
        {t("rollover.intro")}
      </Typography.Paragraph>
      {years.isError && <Alert type="error" showIcon title={t("app.error")} />}

      {step === 0 && (
        <>
          {years.isSuccess && years.data.length < 2 ? (
            <Empty description={t("rollover.noYears")} />
          ) : (
            <Space wrap align="end">
              <Space orientation="vertical" size={4}>
                <Typography.Text>{t("rollover.sourceYear")}</Typography.Text>
                <Select<string | undefined> aria-label={t("rollover.sourceYear")} placeholder={t("rollover.chooseYear")} style={{ width: 200 }} loading={years.isPending} value={source} options={yearOptions} onChange={(value) => value && setPicked((p) => ({ ...p, source: value }))} />
              </Space>
              <Space orientation="vertical" size={4}>
                <Typography.Text>{t("rollover.targetYear")}</Typography.Text>
                <Select<string | undefined> aria-label={t("rollover.targetYear")} placeholder={t("rollover.chooseYear")} style={{ width: 200 }} loading={years.isPending} value={target} options={yearOptions} onChange={(value) => value && setPicked((p) => ({ ...p, target: value }))} />
              </Space>
              <Button type="primary" disabled={!source || !target} onClick={() => source && target && setRequest({ source, target })}>
                {t("rollover.preview")}
              </Button>
            </Space>
          )}
        </>
      )}

      {step === 1 && (
        <>
          <Space wrap>
            <Button onClick={startOver}>{t("rollover.back")}</Button>
            <Button onClick={() => void preview.refetch()} loading={preview.isFetching}>
              {t("rollover.refresh")}
            </Button>
          </Space>
          {preview.isPending && <Spin />}
          {preview.isError && (
            <Alert type="error" showIcon title={(preview.error instanceof ApiError ? preview.error.messages(t("app.error")) : [t("app.error")]).join(" ")} />
          )}
          {preview.isSuccess && rows.length === 0 && <Empty description={t("rollover.empty")} />}
          {preview.isSuccess && rows.length > 0 && (
            <>
              <Alert type="info" showIcon title={t("rollover.leaveHint")} description={t("rollover.noTargetHint")} />
              {groups.map((group) => (
                <RolloverClassCard
                  key={group[0]?.sourceClassId}
                  rows={group}
                  targetClasses={preview.data.targetClasses}
                  choices={choices}
                  errors={rowErrors}
                  compact={!screens.md}
                  onChange={change}
                />
              ))}
              <Space wrap>
                <Tag color="green">{t("rollover.summary.promote", { count: count("Promote") })}</Tag>
                <Tag color="blue">{t("rollover.summary.repeat", { count: count("Repeat") })}</Tag>
                <Tag color="purple">{t("rollover.summary.graduate", { count: count("Graduate") })}</Tag>
                <Tag>{t("rollover.summary.skip", { count: count("Skip") })}</Tag>
              </Space>
              {missing > 0 && <Alert type="warning" showIcon title={t("rollover.missingTargets", { count: missing })} />}
              {decisions.length === 0 && <Alert type="warning" showIcon title={t("rollover.nothingToConfirm")} />}
              {summaryErrors.length > 0 && <Alert type="error" showIcon title={summaryErrors.join(" ")} />}
              <div>
                <Button type="primary" disabled={missing > 0 || decisions.length === 0} loading={confirm.isPending} onClick={askConfirm}>
                  {t("rollover.confirm")}
                </Button>
              </div>
            </>
          )}
        </>
      )}

      {step === 2 && result && (
        <Result
          status="success"
          title={t("rollover.done")}
          subTitle={[
            t("rollover.resultPromoted", { count: Number(result.promoted) }),
            t("rollover.resultRepeated", { count: Number(result.repeated) }),
            t("rollover.resultGraduated", { count: Number(result.graduated) }),
          ].join(" · ")}
          extra={<Button onClick={startOver}>{t("rollover.startOver")}</Button>}
        >
          {result.skipped.length > 0 && (
            <Space orientation="vertical" size={4}>
              <Typography.Text strong>{t("rollover.skippedTitle")}</Typography.Text>
              {result.skipped.map((s) => (
                <Typography.Text key={s.studentId}>{t("rollover.skippedRow", { name: `${s.lastName} ${s.firstName}`, className: s.placedClassName })}</Typography.Text>
              ))}
            </Space>
          )}
        </Result>
      )}
    </Space>
  );
}

/** Rows arrive sorted by grade, class and name; keep that order and split them per source class. */
function groupByClass(rows: RolloverRow[]): RolloverRow[][] {
  const groups = new Map<string, RolloverRow[]>();
  for (const row of rows) groups.set(row.sourceClassId, [...(groups.get(row.sourceClassId) ?? []), row]);
  return [...groups.values()];
}
